import { useState, useCallback, useEffect, useRef } from 'react'
import { questions } from '../data/questions'
import { useAutoSave } from './useAutoSave'
import { AssessmentStorage } from '../utils/assessmentStorage'
import { useOrganisation } from '../contexts/OrganisationContext'

const today = () => new Date().toISOString().split('T')[0]

const toClientAssessment = assessment => ({
  ...assessment,
  maturityLevel: assessment.maturityLevel ?? assessment.maturity_level ?? '',
  activeTab: assessment.activeTab ?? assessment.active_tab ?? 0,
  assessmentDate: assessment.assessmentDate ?? assessment.assessment_date ?? today(),
  summaryRecommendations: assessment.summaryRecommendations ?? assessment.summary_recommendations ?? ''
})

export const useAssessment = () => {
  const { organisationId, engagementId } = useOrganisation()
  const scopeKey = `${organisationId || ''}:${engagementId || ''}`
  const currentScopeRef = useRef(scopeKey)
  currentScopeRef.current = scopeKey
  const [activeTab, setActiveTab] = useState(0)
  const [currentQuestions, setCurrentQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [assessmentName, setAssessmentName] = useState('')
  const [currentAssessmentId, setCurrentAssessmentId] = useState(null)
  const [maturityLevel, setMaturityLevel] = useState('')
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [compliances, setCompliances] = useState({ ML1: 0, ML2: 0, ML3: 0 })
  const [savedAssessments, setSavedAssessments] = useState([])
  const [recommendations, setRecommendations] = useState({})
  const [assessmentDate, setAssessmentDate] = useState(today())
  const [background, setBackground] = useState('')
  const [scope, setScope] = useState('')
  const [limitations, setLimitations] = useState('')
  const [currentAssessmentName, setCurrentAssessmentName] = useState('')
  const [summaryRecommendations, setSummaryRecommendations] = useState('')
  const [hasManualSave, setHasManualSave] = useState(false)

  const generateAssessmentId = useCallback(() => AssessmentStorage.generateId(), [])

  const updateSummaryRecommendations = useCallback(currentAnswers => {
    const controlNames = {
      PA: 'Patch Applications', PO: 'Patch Operating Systems', MFA: 'Multi-factor Authentication',
      RAP: 'Restrict Administrative Privileges', AC: 'Application Control',
      MOCS: 'Configure Microsoft Office macro settings', UAH: 'User Application Hardening', RB: 'Regular Backups'
    }
    const grouped = Object.entries(currentAnswers || {})
      .filter(([, answer]) => answer?.recommendations?.trim())
      .reduce((result, [questionId, answer]) => {
        const control = questionId.split('-')[1]
        const name = controlNames[control] || control
        result[name] = [...(result[name] || []), { id: questionId, recommendations: answer.recommendations }]
        return result
      }, {})
    setSummaryRecommendations(Object.entries(grouped).map(([name, items]) => `${name} Recommendations:\n${items.map(item => `${item.id}:\n${item.recommendations}`).join('\n\n')}\n`).join('\n'))
  }, [])

  const loadAssessment = useCallback(assessmentInput => {
    if (!assessmentInput) return
    const assessment = toClientAssessment(assessmentInput)
    setCurrentAssessmentId(assessment.id)
    setHasManualSave(true)
    setCurrentAssessmentName(assessment.name || '')
    setAssessmentName(assessment.name || '')
    setMaturityLevel(assessment.maturityLevel || 'ML1')
    setAnswers(assessment.answers || {})
    setActiveTab(assessment.activeTab || 0)
    setAssessmentDate(assessment.assessmentDate || today())
    setBackground(assessment.background || '')
    setScope(assessment.scope || '')
    setLimitations(assessment.limitations || '')
    setSummaryRecommendations(assessment.summaryRecommendations || '')
    if (!assessment.summaryRecommendations && assessment.answers) updateSummaryRecommendations(assessment.answers)
  }, [updateSummaryRecommendations])

  const clearAssessmentState = useCallback(() => {
    setActiveTab(0)
    setCurrentQuestions([])
    setAnswers({})
    setAssessmentName('')
    setCurrentAssessmentId(null)
    setMaturityLevel('')
    setTotalQuestions(0)
    setCompliances({ ML1: 0, ML2: 0, ML3: 0 })
    setSavedAssessments([])
    setRecommendations({})
    setAssessmentDate(today())
    setBackground('')
    setScope('')
    setLimitations('')
    setCurrentAssessmentName('')
    setSummaryRecommendations('')
    setHasManualSave(false)
  }, [])

  useEffect(() => {
    let active = true
    clearAssessmentState()
    if (!organisationId || !engagementId) {
      return undefined
    }
    AssessmentStorage.configure({ organisationId, engagementId })
    AssessmentStorage.getAllAssessments()
      .then(items => { if (active) setSavedAssessments(items.map(toClientAssessment)) })
      .catch(error => { if (active) console.error('Assessment list failed:', error.message) })
    return () => { active = false }
  }, [organisationId, engagementId, clearAssessmentState])

  const handleAnswerChange = useCallback((questionId, value, field = 'value') => {
    setAnswers(current => {
      const updated = { ...current, [questionId]: { ...current[questionId], [field]: value } }
      if (field === 'recommendations') updateSummaryRecommendations(updated)
      return updated
    })
  }, [updateSummaryRecommendations])

  useEffect(() => {
    if (Object.keys(answers).length > 0) updateSummaryRecommendations(answers)
  }, [answers, updateSummaryRecommendations])

  const handleDateChange = newDate => {
    setAssessmentDate(newDate)
  }

  const getQuestionsForML = useCallback((controlName, ml) => questions[controlName]?.[ml] || [], [])
  const handleRecommendationsChange = useCallback((controlName, value) => setRecommendations(current => ({ ...current, [controlName]: value })), [])
  const calculateTotalQuestions = useCallback(ml => ml ? Object.values(questions).reduce((total, controlQuestions) => total + (controlQuestions[ml] || []).length, 0) : 0, [])

  useEffect(() => {
    if (maturityLevel) setTotalQuestions(calculateTotalQuestions(maturityLevel))
  }, [maturityLevel, calculateTotalQuestions])

  const deleteAssessment = useCallback(async assessmentNameToDelete => {
    const assessment = savedAssessments.find(item => item.name === assessmentNameToDelete)
    if (!assessment) return
    const operationScope = scopeKey
    const result = await AssessmentStorage.deleteAssessment(assessment.id)
    if (currentScopeRef.current !== operationScope) return
    if (result.success) setSavedAssessments(result.assessments.map(toClientAssessment))
  }, [savedAssessments, scopeKey])

  const updateCompliances = useCallback(currentAnswers => {
    if (!currentAnswers) return
    const next = { ML1: 0, ML2: 0, ML3: 0 }
    Object.values(currentAnswers).forEach(controlAnswers => {
      Object.entries(controlAnswers || {}).forEach(([questionId, answer]) => {
        const level = questionId.match(/ML[123]/)?.[0]
        if (level && answer === 'yes') next[level] += 1
      })
    })
    Object.keys(next).forEach(level => {
      const total = calculateTotalQuestions(level)
      next[level] = total > 0 ? (next[level] / total) * 100 : 0
    })
    setCompliances(next)
  }, [calculateTotalQuestions])

  const createNewAssessment = useCallback(() => {
    setCurrentAssessmentId(generateAssessmentId())
    setHasManualSave(false)
    setAssessmentName('')
    setCurrentAssessmentName('')
    setMaturityLevel('')
    setAnswers({})
    setActiveTab(0)
    setAssessmentDate(today())
    setBackground('')
    setScope('')
    setLimitations('')
    setSummaryRecommendations('')
  }, [generateAssessmentId])

  const saveAssessmentUnified = useCallback(async (saveType = 'manual', nameOverride = null, onSuccess = null, onError = null) => {
    const operationScope = scopeKey
    try {
      if (saveType === 'auto' && !hasManualSave) throw new Error('Auto-save disabled until first manual save')
      const id = currentAssessmentId || generateAssessmentId()
      const name = nameOverride || assessmentName || 'Unnamed Assessment'
      const result = await AssessmentStorage.saveAssessment({
        id, name, maturityLevel, answers: { ...answers }, activeTab, assessmentDate,
        background, scope, limitations, summaryRecommendations
      })
      if (currentScopeRef.current !== operationScope) return
      if (!result.success) throw new Error(result.error)
      const saved = toClientAssessment(result.assessment)
      setCurrentAssessmentId(saved.id)
      setAssessmentName(saved.name)
      setCurrentAssessmentName(saved.name)
      setSavedAssessments(result.allAssessments.map(toClientAssessment))
      if (saveType === 'manual') setHasManualSave(true)
      updateCompliances(saved.answers, activeTab)
      onSuccess?.(`Assessment "${saved.name}" saved successfully.`)
    } catch (error) {
      if (currentScopeRef.current === operationScope) onError?.(`${saveType} save failed: ${error.message}`)
    }
  }, [hasManualSave, currentAssessmentId, generateAssessmentId, assessmentName, maturityLevel, answers, activeTab, assessmentDate, background, scope, limitations, summaryRecommendations, updateCompliances, scopeKey])

  const saveAssessment = useCallback((nameOverride = null, onSuccess = null, onError = null) => {
    if (nameOverride) setAssessmentName(nameOverride)
    return saveAssessmentUnified('manual', nameOverride, onSuccess, onError)
  }, [saveAssessmentUnified])
  const autoSaveAssessment = useCallback((onSuccess = null, onError = null) => saveAssessmentUnified('auto', null, onSuccess, onError), [saveAssessmentUnified])

  const autoSaveData = { assessmentName, maturityLevel, answers, activeTab, assessmentDate, background, scope, limitations, summaryRecommendations }
  const autoSave = useAutoSave((_name, onSuccess, onError) => autoSaveAssessment(onSuccess, onError), autoSaveData, {
    delay: 30000,
    enabled: hasManualSave,
    storageKey: 'autoSaveEnabled'
  })

  return {
    activeTab, setActiveTab, currentQuestions, setCurrentQuestions, answers, setAnswers,
    assessmentName, setAssessmentName, maturityLevel, setMaturityLevel, totalQuestions,
    compliances, setCompliances, savedAssessments, setSavedAssessments, handleAnswerChange,
    getQuestionsForML, recommendations, handleRecommendationsChange, deleteAssessment,
    assessmentDate, setAssessmentDate, handleDateChange, saveAssessment, loadAssessment,
    updateCompliances, background, setBackground, scope, setScope, limitations, setLimitations,
    currentAssessmentName, setCurrentAssessmentName, summaryRecommendations,
    setSummaryRecommendations, updateSummaryRecommendations, autoSave, createNewAssessment,
    hasManualSave
  }
}
